import { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FeaturesHero from "../components/features/FeaturesHero";
import FeatureCategory from "../components/features/FeatureCategory";
import IntegrationSection from "../components/features/IntegrationSection";
import FaqSection from "../components/features/FaqSection";
import FeatureCta from "../components/features/FeatureCta";

function FeaturesPage() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Feature categories with their detailed features
  const featureCategories = [
    {
      id: "smart-categorization",
      title: "Smart Categorization",
      description: "Our AI automatically sorts your emails into intuitive categories so you can focus on what matters.",
      image: "/api/placeholder/600/400",
      features: [
        {
          title: "Automatic Classification",
          description: "AI analyzes email content and automatically sorts into categories like Work, Personal, and Promotions."
        },
        {
          title: "Custom Categories",
          description: "Create your own categories and train our AI to recognize emails that belong in them."
        },
        {
          title: "Rule-Based Filtering",
          description: "Set up rules to automatically sort emails based on sender, subject, or content."
        },
        {
          title: "Visual Organization",
          description: "Color-coded categories make it easy to identify email types at a glance."
        }
      ]
    },
    {
      id: "email-cleanup",
      title: "Email Cleanup",
      description: "Declutter your inbox with intelligent cleanup suggestions and automated actions.",
      image: "/api/placeholder/600/400",
      features: [
        {
          title: "Bulk Action Recommendations",
          description: "Get smart suggestions for groups of emails that can be archived or deleted together."
        },
        {
          title: "One-Click Unsubscribe",
          description: "Identify and unsubscribe from newsletters and promotional emails with a single click."
        },
        {
          title: "Duplicate Detection",
          description: "Automatically find and remove duplicate emails to reduce clutter."
        },
        {
          title: "Scheduled Cleaning",
          description: "Set up automated cleaning routines that run on your preferred schedule."
        }
      ]
    },
    {
      id: "ai-tools",
      title: "AI Productivity Tools",
      description: "Leverage artificial intelligence to handle emails more efficiently than ever before.",
      image: "/api/placeholder/600/400",
      features: [
        {
          title: "Smart Reply",
          description: "Generate contextually appropriate responses with one click based on email content."
        },
        {
          title: "Email Summarization",
          description: "Get concise summaries of long emails to quickly understand the key points."
        },
        {
          title: "Priority Detection",
          description: "AI identifies urgent emails that need your immediate attention."
        },
        {
          title: "Sentiment Analysis",
          description: "Detect the tone and sentiment of emails to prioritize accordingly."
        }
      ]
    },
    {
      id: "attachment-management",
      title: "Attachment Management",
      description: "Never lose track of important files again with intelligent attachment organization.",
      image: "/api/placeholder/600/400",
      features: [
        {
          title: "Attachment Finder",
          description: "Quickly locate emails with attachments using advanced search filters."
        },
        {
          title: "File Type Grouping",
          description: "View attachments grouped by file type (documents, images, etc.)."
        },
        {
          title: "Large Attachment Alerts",
          description: "Get notifications about large attachments that might be filling your storage."
        },
        {
          title: "Attachment Preview",
          description: "Preview attachments without downloading or opening the email."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <div className="fixed inset-0 w-full h-full overflow-hidden z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fff5eb] to-[#f9f6f4]"></div>
        
        {/* Grid pattern background */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.07]" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M0 10 L40 10" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M0 20 L40 20" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M0 30 L40 30" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M10 0 L10 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M20 0 L20 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
              <path d="M30 0 L30 40" stroke="#946e5d" strokeWidth="0.5" fill="none" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      {/* Main Content */}
      <div className="relative z-10">
        <Header />
        
        <main>
          {/* Hero Section */}
          <FeaturesHero />
          
          {/* Feature Categories */}
          {featureCategories.map((category, index) => (
            <FeatureCategory 
              key={category.id}
              id={category.id}
              title={category.title}
              description={category.description}
              image={category.image}
              features={category.features}
              isReversed={index % 2 !== 0}
              index={index}
            />
          ))}
          
          {/* Integration Section */}
          <IntegrationSection />
          
          {/* FAQ Section */}
          <FaqSection />
          
          {/* CTA Section */}
          <FeatureCta />
        </main>
        
        <Footer />
      </div>
    </div>
  );
}

export default FeaturesPage;